// Hook único que carrega eventos do período e computa todas as métricas
// do painel Analytics de Conversão no client. Cache de 60s.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AnalyticsPeriod, periodToSince } from "@/utils/analyticsPeriod";

export interface AnalyticsEventRow {
  id: string;
  event_type: string;
  path: string | null;
  user_id: string | null;
  session_id: string | null;
  device: string | null;
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
}

const MAX_ROWS = 5000;

export function useAnalyticsConversao(period: AnalyticsPeriod) {
  const since = periodToSince(period).toISOString();

  return useQuery({
    queryKey: ["analytics-conversao", period],
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("analytics_events" as any)
        .select("*")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(MAX_ROWS);

      if (error) throw error;
      return (data || []) as unknown as AnalyticsEventRow[];
    },
  });
}

// ============ Helpers de agregação ============

export function countBy<T>(arr: T[], keyFn: (i: T) => string | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const item of arr) {
    const k = keyFn(item);
    if (!k) continue;
    out[k] = (out[k] || 0) + 1;
  }
  return out;
}

export function topEntries(map: Record<string, number>, n = 10): Array<{ key: string; count: number }> {
  return Object.entries(map)
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
}

export function dailyBuckets(
  events: AnalyticsEventRow[],
  predicate: (e: AnalyticsEventRow) => boolean,
  days: number
): Array<{ date: string; count: number }> {
  const buckets: Record<string, number> = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(5, 10); // MM-DD
    buckets[key] = 0;
  }

  for (const e of events) {
    if (!predicate(e)) continue;
    const key = new Date(e.created_at).toISOString().slice(5, 10);
    if (key in buckets) buckets[key]++;
  }

  return Object.entries(buckets).map(([date, count]) => ({ date, count }));
}
